import React, { useEffect, useState } from "react";

import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { Button, Divider, Stack, Tab, TabProps, Tabs, Typography } from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { useActionHook } from "hooks";
import {
  useDealActions,
  useDealIdSelector,
  useDealTitleSelector,
  useUserActions,
  useUserRoleSelector,
  useViewActions
} from "store";

import { RoleName } from "../../../constants";
import { Cashflow } from "./Cashflow/Cashflow";
import { General } from "./General/General";

export enum NotificationActionCollection {
  NOTE = "note",
  TASK = "task",
  FORM = "form",
  FOLDER = "folder",
  FILE = "file",
  QUESTION = "question",
  EMAIL = "incoming_email",
  COMMENT = "comment",
  REPLY = "reply",
  ANSWER = "answer",
  // CRUNCHBASE = "crunchbase",
  INVITE = "invite",
  // DATA = "monitoring_data",
  // COMMITMENT = "commitment",
  CONTACT = "contact",
  CHARACTERISTIC = "characteristic"
}

const dividerStyles: TabProps["sx"] = {
  minWidth: "1px",
  minHeight: "20px",
  maxHeight: "20px"
};

const commonStyles: TabProps["sx"] = {
  minHeight: "20px",
  maxHeight: "25px",
  padding: "0px",
  fontSize: "15px",
  color: "#5d7782",
  minWidth: "50px"
};

export const Activity: React.FC = () => {
  const dealId = useDealIdSelector();
  const dealTitle = useDealTitleSelector();

  const userRole = useUserRoleSelector();
  const isUserInvestor = userRole?.presentation_name === RoleName.Investor;
  const [activeTab, setActiveTab] = useState(isUserInvestor ? 1 : 0); //if user marked as investor so hide general tab

  const { getDealNotificationsAction } = useUserActions();
  const { getLatestMeasureDataTableAction } = useDealActions();
  const { setCreateManyMeasureData } = useViewActions();

  const {
    action: getDealActivity,
    data: activity = [],
    loading: activityLoading
  } = useActionHook(getDealNotificationsAction);

  const { action: getLatestData, data, loading: dataLoading } = useActionHook(getLatestMeasureDataTableAction);

  useEffect(() => {
    if (!dealId) return;
    !activeTab && getDealActivity(dealId);
    !!activeTab && getLatestData(dealId);
  }, [dealId, activeTab]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Stack
      sx={{
        p: "16px",
        borderRadius: "8px",
        bgcolor: "#FFF",
        gap: "8px",
        boxShadow: "0 1px 2px 0 #1212171a",
        overflow: "hidden",
        flexGrow: 1
      }}
    >
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ color: "#071F35", fontSize: "18px", fontWeight: 500 }}>Latest Activity</Typography>
        {!!activeTab && (
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Button onClick={() => dealId && setCreateManyMeasureData({ dealId, dealTitle })} variant="contained">
              Add Value
            </Button>
          </RoleComponentGuard>
        )}
      </Stack>
      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}>
        <Tabs value={activeTab} onChange={handleChange} sx={{ mt: 2, minHeight: "25px", mb: 2 }}>
          <Tab
            value={0}
            label="General"
            iconPosition="start"
            icon={<GridViewOutlinedIcon sx={{ fontSize: "14px" }} />}
            sx={commonStyles}
          />
          <Tab
            sx={dividerStyles}
            label=""
            icon={<Divider sx={{ width: "1px", height: "16px", backgroundColor: "#dae0e2" }} />}
            disabled
          />
          <Tab
            value={1}
            label="Cashflow"
            iconPosition="start"
            icon={<InsertChartOutlinedIcon sx={{ fontSize: "15px" }} />}
            sx={commonStyles}
          />
        </Tabs>
      </RoleComponentGuard>
      {!activeTab && <General activity={activity} loading={activityLoading} />}
      {!!activeTab && data && <Cashflow data={data.rows} loading={dataLoading} />}
    </Stack>
  );
};
